import { AlertTriangle, X, ArrowRight, Check, History } from "lucide-react";

// Side-by-side compare that gates the "link a different quote to this booking"
// action. Also used in read-only mode from the Activity log to review a past
// swap. The parent supplies the preview payload from either
// /admin/booking/{ref}/link-quote-preview (interactive) or the audit event's
// `after` field (read-only).
export function LinkQuoteCompareModal({ preview, busy, readOnly = false, onConfirm, onCancel }) {
  if (!preview) return null;
  const { previous_quote: prev, new_quote: next, diff = [], booking_reference } = preview;
  const changedCount = diff.filter((d) => d.changed).length;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#12263A]/70 flex items-center justify-center p-4" data-testid="link-quote-compare-modal">
      <div className="bg-white w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="flex items-start justify-between p-6 border-b border-[#E5E7EB]">
          <div className="flex items-start gap-3">
            <div className={`mt-1 border p-2 ${readOnly ? "bg-[#F0F2F5] border-[#D1D5DB]" : "bg-[#FFF7E0] border-[#F4E1A0]"}`}>
              {readOnly ? <History size={20} className="text-[#12263A]" /> : <AlertTriangle size={20} className="text-[#B77A00]" />}
            </div>
            <div>
              <h2 className="text-xl font-black text-[#12263A]">
                {readOnly
                  ? `Quote swap on job ${booking_reference}`
                  : `Swap the quote linked to job ${booking_reference}?`}
              </h2>
              <p className="text-sm text-[#6B7280] mt-1">
                {readOnly
                  ? "Read-only history — this shows the exact side-by-side comparison at the moment the swap was committed."
                  : (<>You&apos;re about to replace the quote currently attached to this booking. The old quote will be auto-archived (one-click restore is available from the Activity log).</>)}
              </p>
            </div>
          </div>
          <button data-testid="link-quote-compare-close" onClick={onCancel} className="text-[#9CA3AF] hover:text-[#12263A] shrink-0" aria-label="Close"><X size={20} /></button>
        </div>

        <div className="overflow-auto flex-1">
          <div className="grid grid-cols-2 border-b border-[#E5E7EB] sticky top-0 bg-white z-10">
            <div className="p-4 border-r border-[#E5E7EB]">
              <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF] font-bold">{readOnly ? "Was linked" : "Currently linked"}</div>
              <div className="font-mono text-sm text-[#12263A] mt-0.5">{prev?.reference || prev?.id || "—"}</div>
              <div className="text-xs text-[#6B7280] mt-0.5">Created {fmtDate(prev?.created_at)}</div>
            </div>
            <div className="p-4 bg-[#F7F5EE]">
              <div className="text-[10px] uppercase tracking-widest text-[#C79A3B] font-bold flex items-center gap-1.5"><ArrowRight size={11} /> {readOnly ? "Replaced with" : "New quote"}</div>
              <div className="font-mono text-sm text-[#12263A] mt-0.5">{next?.reference || next?.id || "—"}</div>
              <div className="text-xs text-[#6B7280] mt-0.5">Created {fmtDate(next?.created_at)}</div>
            </div>
          </div>

          <table className="w-full text-sm">
            <tbody>
              {diff.map((row) => (
                <tr key={row.label} className={`border-b border-[#F1F3F5] ${row.changed ? "bg-[#FFF9E8]" : ""}`} data-testid={`link-diff-row-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <td className="p-3 text-[#6B7280] w-40 border-r border-[#E5E7EB] text-xs uppercase tracking-wide font-bold">{row.label}{row.changed && <span className="ml-1.5 text-[#B77A00]">•</span>}</td>
                  <td className={`p-3 border-r border-[#E5E7EB] ${row.changed ? "line-through text-[#9CA3AF]" : "text-[#12263A]"}`}>{row.previous ?? "—"}</td>
                  <td className={`p-3 ${row.changed ? "text-[#12263A] font-bold bg-[#FDF2C9]" : "text-[#12263A]"}`}>{row.next ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-6 border-t border-[#E5E7EB] flex items-center justify-between gap-4 bg-[#FAFBFC]">
          <div className="text-xs text-[#6B7280]" data-testid="link-diff-summary">
            {changedCount === 0
              ? "The two quotes are identical — nothing will change if you confirm."
              : <span><span className="font-bold text-[#B77A00]">{changedCount}</span> field{changedCount !== 1 ? "s" : ""} {readOnly ? "changed" : "will change"}.</span>}
          </div>
          <div className="flex gap-3">
            {readOnly ? (
              <button data-testid="link-quote-compare-close-btn" onClick={onCancel} className="de-btn-primary">Close</button>
            ) : (
              <>
                <button data-testid="link-quote-compare-cancel" disabled={busy} onClick={onCancel} className="de-btn-secondary">Cancel</button>
                <button data-testid="link-quote-compare-confirm" disabled={busy} onClick={onConfirm} className="de-btn-primary">
                  {busy ? "Linking…" : (<><Check size={16} /> Confirm swap</>)}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
